import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X, ArrowRightLeft, Eye, EyeOff, User } from 'lucide-react';
import PhotoUpload from './PhotoUpload';
import { useSiteData } from '@/context/SiteDataContext';

/**
 * LeadersEditor
 * -------------
 * Grouped-by-section admin editor. Instead of one big list, leaders are
 * organized under their section (Prelate, Cabinet, District, Consultants, …).
 * Admin can add, edit, move within a group (up/down), move between groups
 * (left = previous section, right = next section, or via a dropdown), hide,
 * or remove leaders.
 */

const DEFAULT_SECTIONS = [
  'Episcopal Leadership',
  'Executive Administration',
  'District Leadership',
  'Pastors & Elders Council',
  'Ordination & Ministerial Development',
  'Adjutancy',
  'Jurisdictional Department Presidents',
];

const empty = (section = '') => ({
  name: '', role: '', photo: '', photoScale: 1, photoPosition: '50% 10%',
  bio: '', responsibilities: [], order: 999, section, visible: true,
});

export default function LeadersEditor() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const { refresh } = useSiteData();

  const load = async () => {
    try {
      const { data } = await api.get('/site/leaders');
      setItems(data || []);
    } catch { toast.error('Failed to load leaders'); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const buckets = new Map();
    const knownSections = new Set(DEFAULT_SECTIONS);
    (items || []).forEach(l => {
      const s = l.section || 'Leadership';
      knownSections.add(s);
      if (!buckets.has(s)) buckets.set(s, []);
      buckets.get(s).push(l);
    });
    // Ensure known/default sections show even when empty for easier assignment.
    DEFAULT_SECTIONS.forEach(s => { if (!buckets.has(s)) buckets.set(s, []); });
    // Sort leaders inside each group by order.
    for (const [, arr] of buckets) arr.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    // Return groups in DEFAULT_SECTIONS order, then any custom ones alphabetically.
    const known = DEFAULT_SECTIONS.filter(s => buckets.has(s));
    const extras = [...buckets.keys()].filter(s => !DEFAULT_SECTIONS.includes(s)).sort();
    return [...known, ...extras].map(s => ({ section: s, leaders: buckets.get(s) || [] }));
  }, [items]);

  const sectionOptions = grouped.map(g => g.section);

  const save = async () => {
    if (!editing.name || !editing.role) { toast.error('Name and role required'); return; }
    setBusy(true);
    try {
      const payload = { ...editing };
      payload.photoScale = parseFloat(payload.photoScale) || 1;
      if (typeof payload.responsibilities === 'string') {
        payload.responsibilities = payload.responsibilities.split('\n').map(s => s.trim()).filter(Boolean);
      }
      if (editing.id) await api.patch(`/site/leaders/${editing.id}`, payload);
      else await api.post('/site/leaders', payload);
      toast.success('Saved · live');
      setEditing(null);
      await load();
      refresh?.(['leaders']);
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this leader?')) return;
    await api.delete(`/site/leaders/${id}`);
    toast.success('Deleted');
    await load(); refresh?.(['leaders']);
  };

  const patchOne = async (id, patch) => {
    await api.patch(`/site/leaders/${id}`, patch);
    await load(); refresh?.(['leaders']);
  };

  const moveWithinGroup = async (leader, delta) => {
    const groupLeaders = (grouped.find(g => g.section === (leader.section || 'Leadership'))?.leaders) || [];
    const idx = groupLeaders.findIndex(l => l.id === leader.id);
    const swap = idx + delta;
    if (swap < 0 || swap >= groupLeaders.length) return;
    // Normalise every leader's `order` in this group to its current index first,
    // then swap the two positions. This defeats the "everyone's order = 999"
    // stalemate that made some leaders un-movable.
    const reindexed = groupLeaders.map((l, i) => ({ id: l.id, order: i }));
    [reindexed[idx].order, reindexed[swap].order] = [reindexed[swap].order, reindexed[idx].order];
    await Promise.all(reindexed.map(r => api.patch(`/site/leaders/${r.id}`, { order: r.order })));
    await load(); refresh?.(['leaders']);
    toast.success(delta < 0 ? 'Moved up' : 'Moved down');
  };

  const moveToSection = async (leader, direction /* -1 = previous, +1 = next */) => {
    const idx = sectionOptions.indexOf(leader.section || sectionOptions[0]);
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= sectionOptions.length) return;
    await patchOne(leader.id, { section: sectionOptions[nextIdx] });
    toast.success(`Moved to ${sectionOptions[nextIdx]}`);
  };

  const respsAsString = (r) => Array.isArray(r) ? r.join('\n') : (r || '');
  const total = items.length;

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-serif text-xl text-royal font-bold">Leadership <span className="text-charcoal-muted text-sm font-normal">({total} across {grouped.length} groups)</span></h3>
          <p className="text-xs text-charcoal-muted mt-1">Leaders are grouped by <em>Section</em>. Use ▲▼ to reorder within a group, ◀▶ to move between groups, the eye to hide, or the trash to delete.</p>
        </div>
        <button data-testid="leaders-add-btn" onClick={() => setEditing(empty(sectionOptions[0]))} className="btn-royal text-sm py-2 px-4 flex items-center gap-1"><Plus className="w-4 h-4" /> Add Leader</button>
      </div>

      <div className="space-y-4">
        {grouped.map(({ section, leaders }) => {
          const open = collapsed[section] !== true;
          return (
            <div key={section} data-testid={`leaders-group-${section.replace(/\W+/g, '-').toLowerCase()}`} className="bg-royal/[.03] border border-royal/10 rounded-lg overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between bg-royal/5 border-b border-royal/10">
                <div className="flex items-center gap-2">
                  <button onClick={() => setCollapsed(c => ({ ...c, [section]: open }))} className="text-royal hover:text-gold">
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="font-serif text-royal font-bold">{section}</div>
                  <div className="text-xs text-charcoal-muted">· {leaders.length} {leaders.length === 1 ? 'leader' : 'leaders'}</div>
                </div>
                <button onClick={() => setEditing(empty(section))}
                  data-testid={`leaders-add-in-${section.replace(/\W+/g, '-').toLowerCase()}`}
                  className="text-xs px-3 py-1 border border-royal text-royal rounded-full hover:bg-royal hover:text-white transition">
                  <Plus className="w-3 h-3 inline" /> Add to {section}
                </button>
              </div>
              {open && (
                <div className="p-3 space-y-2">
                  {leaders.length === 0 && <div className="text-xs text-charcoal-muted italic px-2">No leaders yet in this group.</div>}
                  {leaders.map((l, i) => {
                    const hidden = l.visible === false;
                    return (
                      <div key={l.id}
                           data-testid={`leader-row-${l.id}`}
                           className={`bg-white border rounded-lg p-3 flex items-center gap-3 ${hidden ? 'opacity-60' : ''}`}>
                        <div className="w-12 h-12 rounded overflow-hidden bg-royal/10 flex-shrink-0 flex items-center justify-center">
                          {l.photo
                            ? <img src={l.photo} alt="" className="w-full h-full object-cover"
                                   style={{ objectPosition: l.photoPosition || 'center top', transform: l.photoScale ? `scale(${l.photoScale})` : undefined, transformOrigin: 'center top' }} />
                            : <User className="w-5 h-5 text-royal" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-royal truncate flex items-center gap-2">
                            {l.name}
                            {hidden && <span className="text-[10px] uppercase tracking-widest bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Hidden</span>}
                          </div>
                          <div className="text-xs text-charcoal-muted truncate">{l.role}</div>
                        </div>
                        <div className="flex items-center gap-1 flex-wrap justify-end">
                          <button title="Move up" onClick={() => moveWithinGroup(l, -1)} disabled={i === 0} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                          <button title="Move down" onClick={() => moveWithinGroup(l, 1)} disabled={i === leaders.length - 1} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                          <button title="Move to previous group" onClick={() => moveToSection(l, -1)} disabled={sectionOptions.indexOf(l.section || sectionOptions[0]) === 0} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                          <button title="Move to next group" onClick={() => moveToSection(l, +1)} disabled={sectionOptions.indexOf(l.section || sectionOptions[0]) >= sectionOptions.length - 1} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                          <select value={l.section || section} onChange={(e) => patchOne(l.id, { section: e.target.value })}
                            title="Move to any group"
                            data-testid={`leader-section-${l.id}`}
                            className="text-xs px-2 py-1 border rounded max-w-[140px]">
                            {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <button title={hidden ? 'Show' : 'Hide'} onClick={() => patchOne(l.id, { visible: hidden })} className="p-1.5 hover:bg-gray-100 rounded">
                            {hidden ? <EyeOff className="w-4 h-4 text-red-500" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button onClick={() => setEditing({ ...l, responsibilities: respsAsString(l.responsibilities) })}
                                  data-testid={`leader-edit-${l.id}`}
                                  className="text-xs px-3 py-1.5 bg-royal text-white rounded">Edit</button>
                          <button onClick={() => remove(l.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <LeaderModal
          value={editing} onChange={setEditing}
          onClose={() => setEditing(null)} onSave={save} busy={busy}
          sectionOptions={sectionOptions}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modal — kept lean; retains photo controls, section select, and public fields.
// -----------------------------------------------------------------------------
function LeaderModal({ value, onChange, onClose, onSave, busy, sectionOptions }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full my-8 max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="font-serif text-xl text-royal font-bold">{value.id ? 'Edit' : 'Add'} Leader</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-5">
          <div className="space-y-4">
            <PhotoUpload value={value.photo} onChange={(v) => set('photo', v)} category="leaders" label="Portrait" />
            <div>
              <div className="text-sm font-semibold text-royal mb-1">Zoom ({value.photoScale}x)</div>
              <input type="range" min="0.8" max="3" step="0.05" value={value.photoScale}
                onChange={(e) => set('photoScale', parseFloat(e.target.value))} className="w-full" />
            </div>
            <div>
              <div className="text-sm font-semibold text-royal mb-1">Vertical position ({value.photoPosition})</div>
              <input type="range" min="0" max="100" step="1"
                value={parseInt((value.photoPosition || '50% 10%').split(' ')[1]) || 10}
                onChange={(e) => set('photoPosition', `50% ${e.target.value}%`)} className="w-full" />
              <div className="text-xs text-charcoal-muted mt-1">Lower % = show top of photo; higher = show more body.</div>
            </div>
            <div className="aspect-square rounded overflow-hidden relative bg-[#F1E9D6] border">
              {value.photo && <img src={value.photo} alt="preview" className="w-full h-full object-cover"
                style={{ objectPosition: value.photoPosition || 'center top', transform: value.photoScale ? `scale(${value.photoScale})` : undefined, transformOrigin: 'center top' }} />}
            </div>
          </div>
          <div className="space-y-3">
            <F label="Name"><input value={value.name} onChange={(e) => set('name', e.target.value)} data-testid="leader-name-input" className="w-full px-3 py-2 border rounded" /></F>
            <F label="Role / Title"><input value={value.role} onChange={(e) => set('role', e.target.value)} data-testid="leader-role-input" className="w-full px-3 py-2 border rounded" /></F>
            <F label="Section / Group (organizes on public + admin)">
              <div className="flex gap-2">
                <select value={sectionOptions.includes(value.section) ? value.section : ''} onChange={(e) => set('section', e.target.value)} className="px-3 py-2 border rounded text-sm">
                  <option value="">— Custom —</option>
                  {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input placeholder="Or type a group name" value={value.section || ''} onChange={(e) => set('section', e.target.value)}
                  data-testid="leader-section-input"
                  className="flex-1 px-3 py-2 border rounded text-sm" />
              </div>
            </F>
            <F label="Biography"><textarea rows="4" value={value.bio || ''} onChange={(e) => set('bio', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            <F label="Ministry Responsibilities (one per line)"><textarea rows="3" value={value.responsibilities || ''} onChange={(e) => set('responsibilities', e.target.value)} className="w-full px-3 py-2 border rounded" /></F>
            <div className="pt-3 border-t">
              <div className="text-sm font-bold text-royal mb-2">Assignments</div>
              <input placeholder="Department id (e.g. youth, women)" value={value.department_id || ''} onChange={(e) => set('department_id', e.target.value)} className="w-full px-3 py-2 border rounded text-sm mb-1" />
              <input placeholder="Role in that department (e.g. President)" value={value.department_role || ''} onChange={(e) => set('department_role', e.target.value)} className="w-full px-3 py-2 border rounded text-sm mb-1" />
              <input placeholder="District id (optional)" value={value.district_id || ''} onChange={(e) => set('district_id', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div className="pt-2 border-t">
              <div className="text-sm font-bold text-royal mb-2">Public Contact</div>
              <input placeholder="Email" value={value.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} className="w-full px-3 py-2 border rounded text-sm mb-1" />
              <input placeholder="Phone" value={value.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} className="w-full px-3 py-2 border rounded text-sm mb-1" />
              <input placeholder="Facebook URL" value={value.facebook_url || ''} onChange={(e) => set('facebook_url', e.target.value)} className="w-full px-3 py-2 border rounded text-sm mb-1" />
              <input placeholder="Instagram URL" value={value.instagram_url || ''} onChange={(e) => set('instagram_url', e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <label className="flex items-center gap-2 pt-2 cursor-pointer">
              <input type="checkbox" checked={value.visible !== false} onChange={(e) => set('visible', e.target.checked)}
                data-testid="leader-visible-toggle" className="w-4 h-4 accent-royal" />
              <span className="text-sm font-semibold text-royal">Visible on public site</span>
            </label>
          </div>
        </div>
        <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white z-10">
          <button onClick={onClose} className="px-4 py-2 text-charcoal-muted">Cancel</button>
          <button onClick={onSave} disabled={busy} data-testid="leader-save-btn" className="btn-royal">{busy ? 'Saving…' : 'Save · Publish'}</button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) { return (<div><div className="text-sm font-semibold text-royal mb-1">{label}</div>{children}</div>); }
